import Swal from "sweetalert2";

/* ─────────────────────────────────────────────────────────────
 * Shared helper: push a Swal container above MUI modals.
 * MUI modal z-index = 1300; we need to exceed that.
 * ───────────────────────────────────────────────────────────── */
const raiseSwalAboveModal = (popup) => {
  const container = popup?.closest?.(".swal2-container");
  if (container) container.style.zIndex = "9999";
};

/**
 * Returns true when the API message signals that a previously-stored cartId
 * has already been closed on the server.
 *
 * Matches both envelope variants:
 *   • { status:"ERROR", message:"Error : Cart is already closed please create new cart." }
 *   • err.response.data.message: "Unable to fetch cart details. :: Cart is already closed…"
 */
export const isStaleCartError = (msg) => {
  if (!msg) return false;
  return String(msg).toLowerCase().includes("cart is already closed");
};

/**
 * Wipes every localStorage key that is tied to the current cart / order session.
 * Call this whenever a stale cart is detected so the next cart-creation attempt
 * starts with a clean slate.
 */
export const clearStaleCartStorage = () => {
  const STALE_KEYS = [
    "CartId",
    "cartOrderType",
    "cartPricingMode",
    "b2bPartnerName",
    "b2bPartnerId",
    "cartSalesOrderPartnerId",
    "cartSalesOrderPartnerName",
    "customer_details",
    "loyaltyDetails",
  ];
  STALE_KEYS.forEach((k) => localStorage.removeItem(k));
};

/**
 * Shows the "Stale Cart Detected and Cleared" Swal dialog.
 * Returns the Swal promise so callers can await it if needed.
 */
export const showStaleCartDialog = () =>
  Swal.fire({
    title: "Stale Cart Detected and Cleared",
    html:
      "A previous cart session was already closed on the server and has been cleared." +
      "<br/>Press <strong>OK</strong> to continue.",
    icon: "warning",
    confirmButtonText: "OK",
    customClass: {
      confirmButton: "button button-success",
      container: "close-session-swal-container",
    },
    buttonsStyling: false,
    allowOutsideClick: false,
    didOpen: raiseSwalAboveModal,
  });

/**
 * Returns true when the API message signals that there is no active cart
 * for the given cartId (cart not found / not created yet).
 *
 * Matches the server messages:
 *   { es: 2, message: "No Cart found for this cart Id." }
 *   { es: 1, message: "...CartEntity.getCartProducts()\" because \"cartEntity\" is null" }
 */
export const isNoCartError = (msg) => {
  if (!msg) return false;
  const lower = String(msg).toLowerCase();
  return (
    lower.includes("no cart found") ||
    // Java NPE thrown when cartId is null / cart doesn't exist on the manual-add endpoint
    lower.includes("cartentity") && lower.includes("null")
  );
};

/**
 * Shows a "No Active Cart Found" Swal dialog.
 * Rendered above MUI modals (z-index 9999) so it is always visible
 * even when triggered from inside a product-lookup popup.
 *
 * When the user confirms, dispatches a CustomEvent so that whichever
 * layout is currently mounted can open the customer-addition modal and
 * let the operator create a cart without manual navigation.
 */
export const showNoCartDialog = async () => {
  const result = await Swal.fire({
    title: "",
    html:
      `<div style="text-align:center;padding:4px 0 16px;">` +
        `<div style="width:64px;height:64px;border-radius:50%;background:#fff7ed;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;border:2px solid #fed7aa;">` +
          `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
            `<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>` +
            `<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>` +
          `</svg>` +
        `</div>` +
        `<p style="font-size:18px;font-weight:700;color:#0f172a;margin:0 0 6px;">No Active Cart Found</p>` +
        `<p style="font-size:13px;color:#64748b;margin:0 0 4px;line-height:1.5;">` +
          `Please add a customer and create a cart before adding products.` +
        `</p>` +
      `</div>` +
      `<div style="background:#fff7ed;border:1.5px solid #fed7aa;border-radius:12px;padding:14px 16px;text-align:left;">` +
        `<p style="margin:0 0 4px;font-size:13px;font-weight:700;color:#c2410c;display:flex;align-items:center;gap:6px;">` +
          `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>` +
          `No active cart` +
        `</p>` +
        `<p style="margin:0;font-size:12.5px;color:#9a3412;line-height:1.65;">` +
          `Use the <strong>Add Customer</strong> button to search for or add a customer — a cart will be created automatically.` +
        `</p>` +
      `</div>`,
    showCancelButton: false,
    confirmButtonText: "Add Customer",
    width: "460px",
    allowOutsideClick: false,
    didOpen: (popup) => {
      raiseSwalAboveModal(popup);
      const confirmBtn = popup.querySelector(".swal2-confirm");
      if (confirmBtn)
        confirmBtn.style.cssText =
          "background:#f97316;color:#fff;border:none;padding:10px 28px;" +
          "border-radius:8px;font-weight:700;font-size:13.5px;cursor:pointer;min-width:140px;margin-top:16px;";
    },
  });

  /* On confirm, signal whichever POS layout is mounted to open the
   * customer-addition modal so the operator can create a cart immediately. */
  if (result.isConfirmed) {
    window.dispatchEvent(new CustomEvent("pallet:open-customer-modal"));
  }
};
